import { lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import { reloadForStaleChunk, reloadPending } from "@/lib/chunk-reload";
import { AppShell } from "@/components/AppShell";
import { RouteErrorFallback } from "@/components/RouteErrorFallback";
import {
  LegacyRedirect,
  OrgGuard,
  RequireAuth,
  RequireRole,
  RequireWriter,
  RootRedirect,
  SettingsTabRedirect,
} from "@/route-gates";
import { OverviewPage } from "@/pages/Overview";
import { PrimitivesDemo } from "@/pages/Primitives";
import { NotFoundPage } from "@/pages/Stubs";
import { AutopilotPage } from "@/pages/autopilot/AutopilotPage";
import { TesterPage } from "@/pages/cpp-tester/TesterPage";

// Lazy import that self-heals stale tabs after a deploy. When a new
// build ships, the hashed chunk filenames change and the OLD chunks are
// deleted from the server. A tab still running the previous build then
// does `import("./RulesPage-OLDHASH.js")` → 404 → React throws
// "Importing a module script failed" / a blank error route. Instead of
// surfacing that to the user, `reloadForStaleChunk` (shared with the
// global `vite:preloadError` listener + the top-level error boundary in
// main.tsx) reloads once — the fresh index.html, served no-store,
// points at the current chunk hashes. The self-expiring time guard
// lives in @/lib/chunk-reload so all three call sites can't disagree.
function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      // Never resolve while a reload is on its way — ours or the one the
      // `vite:preloadError` listener issued a moment earlier for this
      // chunk's dependencies. Throwing here handed the error to React
      // Router's boundary for the seconds until that reload landed.
      if (reloadForStaleChunk() || reloadPending()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

// The payment page: its own chunk, because nobody logged in ever loads it
// and the person who does has no session at all.
const PayPage = lazyWithReload(() => import("@/pages/PayPage"));

// Keyword Parser + the full-screen results review: own chunks, opened only
// by orgs with the rollout flag.
const AsoPage = lazyWithReload(() =>
  import("@/pages/aso/AsoPage").then((m) => ({ default: m.AsoPage })),
);
const KeywordParserPage = lazyWithReload(() =>
  import("@/pages/keyword-parser/KeywordParserPage").then((m) => ({ default: m.KeywordParserPage })),
);
const ResultsScreen = lazyWithReload(() =>
  import("@/pages/keyword-parser/ResultsScreen").then((m) => ({ default: m.ResultsScreen })),
);

// Rules is the largest single section of the SPA — CreateRuleModal +
// RuleLogsView + ApplyToModal together are ~3k LOC. Most users never
// open this route; lazy-load it so its bundle isn't paid for on the
// initial /overview load. Same `RulesPage` named export, just resolved
// asynchronously on first navigation to /rules.
const RulesPage = lazyWithReload(() =>
  import("@/pages/rules/RulesPage").then((m) => ({ default: m.RulesPage })),
);

// Campaign Studio (Phase A/B/C/D) — separate lazy chunk. The full
// surface (3 flow forms + NameTemplateModal + CsvUploadPopup + 2
// multi-pickers + Popover + primitives + the JS name-template
// engine mirror) added ~130 KB uncompressed to the main bundle
// after 2026-05-26's deploys. Most users land on /overview or /ads
// and never touch /campaign-studio, so paying that cost on every
// /ads load was wasted bandwidth. Splitting cuts ~30-40 KB
// gzipped off the initial parse for non-Studio sessions.
const CampaignStudioPage = lazyWithReload(() =>
  import("@/pages/campaign-studio/CampaignStudioPage").then((m) => ({
    default: m.CampaignStudioPage,
  })),
);

// Lightweight Suspense fallback for lazy routes — avoids a jarring
// white flash while the chunk is downloading. The route element is
// wrapped here once so callers don't repeat the boilerplate.
const lazyEl = (el: JSX.Element) => (
  <Suspense fallback={<div style={{ padding: 32, color: "var(--c-text-3)" }}>Loading…</div>}>
    {el}
  </Suspense>
);
import { CampaignDetailPage } from "@/pages/ads/CampaignDetailPage";
import { CampaignsPage } from "@/pages/ads/CampaignsPage";
import { EventMappingPage } from "@/pages/settings/EventMappingPage";
import { SettingsLayout } from "@/pages/settings/SettingsLayout";
import { AuditLogPage } from "@/pages/settings/AuditLogPage";
import { ChangePasswordPage } from "@/pages/auth/ChangePassword";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPassword";
import { LoginPage } from "@/pages/auth/Login";
import { ResetPasswordPage } from "@/pages/auth/ResetPassword";

const settingsRedirect = (tab: string) => <SettingsTabRedirect tab={tab} />;

export const router = createBrowserRouter([
  {
    // One boundary over every route: the router's default page must never
    // be what a person sees (see RouteErrorFallback).
    errorElement: <RouteErrorFallback />,
    children: [
  // Public auth routes — no org id required.
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },

  // `must_change_password` escape hatch — authenticated but pre-shell.
  { path: "/change-password", element: <ChangePasswordPage /> },

  // Primitives demo — public for now so we can smoke-test visuals
  // without a running backend. Remove before GA.
  { path: "/_primitives", element: <PrimitivesDemo /> },

  // Оплата. Публичный маршрут: человек приходит по ссылке после звонка,
  // сессии у него нет и быть не должно. Адрес совпадает с default payment
  // link в кабинете Paddle — менять его здесь нельзя, не поменяв там.
  { path: "/pay", element: lazyEl(<PayPage />) },

  // Root: redirect to the user's active org overview.
  { path: "/", element: <RootRedirect /> },

  // Authenticated app, scoped under /:orgPublicId/*
  {
    path: "/:orgPublicId",
    element: <RequireAuth />,
    children: [
      {
        element: <OrgGuard />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },

              { path: "overview", element: <OverviewPage /> },

              // Ads Manager
              { path: "ads", element: <CampaignsPage /> },
              {
                path: "ads/campaigns/:campaignId",
                element: <CampaignDetailPage />,
              },

              { path: "aso", element: lazyEl(<AsoPage />) },
              { path: "keywords", element: lazyEl(<KeywordParserPage />) },
              { path: "keywords/runs/:runId", element: lazyEl(<ResultsScreen />) },
              { path: "tester", element: <TesterPage /> },

              // Settings — single tab-strip page. Tab selection lives
              // in `?tab=X`. Defaults to ad-account when no tab param.
              { path: "settings", element: <SettingsLayout /> },

              // Legacy redirects — old per-page paths forward to the
              // matching tab on the new single-page Settings.
              {
                path: "settings/profile",
                element: settingsRedirect("profile"),
              },
              {
                path: "settings/integrations",
                element: settingsRedirect("integrations"),
              },
              {
                path: "settings/asa-integration",
                element: settingsRedirect("ad-account"),
              },
              {
                path: "settings/users",
                element: settingsRedirect("roles"),
              },
              {
                path: "settings/audit",
                element: settingsRedirect("other"),
              },

              // Event Mapping is the only deep-route under Settings —
              // user clicks "Review Event Mapping" on a provider card,
              // a full page renders with breadcrumbs back.
              {
                path: "settings/event-mapping/:integrationId",
                element: <EventMappingPage />,
              },
              // Legacy event-mapping path
              {
                path: "settings/integrations/:integrationId/event-mapping",
                element: <EventMappingPage />,
              },

              // Writers: account admin / ads_manager OR a per-app writer
              // (§51 — account-role 'viewer' with per-app Ads-Manager).
              {
                element: <RequireWriter />,
                children: [
                  {
                    path: "campaign-studio",
                    element: lazyEl(<CampaignStudioPage />),
                  },
                  { path: "rules", element: lazyEl(<RulesPage />) },
                  { path: "autopilot", element: <AutopilotPage /> },
                ],
              },

              // Admin-only deep links (audit log lives in Other tab as
              // a future expansion; until then keep the page reachable
              // via direct URL).
              {
                element: <RequireRole allow={["admin"]} />,
                children: [
                  { path: "settings/audit-log", element: <AuditLogPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // Legacy org-less paths — forward to the active org's scoped URL.
  // Listed explicitly so arbitrary /:something URLs still 404 cleanly
  // instead of getting swallowed here.
  { path: "/overview", element: <LegacyRedirect /> },
  { path: "/ads", element: <LegacyRedirect /> },
  { path: "/ads/*", element: <LegacyRedirect /> },
  { path: "/aso", element: <LegacyRedirect /> },
  { path: "/keywords", element: <LegacyRedirect /> },
  { path: "/tester", element: <LegacyRedirect /> },
  { path: "/rules", element: <LegacyRedirect /> },
  { path: "/autopilot", element: <LegacyRedirect /> },
  { path: "/settings", element: <LegacyRedirect /> },
  { path: "/settings/*", element: <LegacyRedirect /> },

  { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
